/**
 * MobileScannerCamera (P4 Mobile-Scan, Feb 2026 — Iter 73h)
 *
 * Live-Camera-Preview mit Capture für mobiles Beleg-Scannen.
 * – Stream wird vom Parent geliefert (über onClick getUserMedia, damit die
 *   User-Gesture-Chain auf iOS Safari intakt bleibt — sonst silent stuck).
 * – Capture liefert JPEG-Blob (~85% quality).
 *
 * Auto-Crop wurde in dieser Iteration entfernt: jscanify+OpenCV.js (10 MB) waren
 * auf iOS Safari unzuverlässig (silent stuck, blockierte UI). Backlog: server-seitiges
 * Cropping (FastAPI + OpenCV-Python) als robusterer Ersatz.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Camera as CameraIcon, AlertTriangle, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MAX_UPLOAD_SIDE = 2500;


export default function MobileScannerCamera({ stream, onCapture, onCancel, pageCount = 0, quickMode = false, quickQueuedCount = 0 }) {
  const videoRef = useRef(null);

  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [flash, setFlash] = useState(false); // kurzer Blitz als Auslöse-Feedback

  // Stream → Video-Element. Wird so synchron wie möglich gemacht, damit iOS
  // den Bezug zur ursprünglichen User-Gesture nicht verliert.
  useEffect(() => {
    if (!stream) {
      setError('Kamera-Stream fehlt. Bitte „Scannen starten" erneut tippen.');
      return;
    }
    const v = videoRef.current;
    if (!v) return;

    // iOS-Safari-Pflichtattribute
    v.setAttribute('playsinline', 'true');
    v.setAttribute('webkit-playsinline', 'true');
    v.muted = true;
    v.autoplay = true;

    const onPlaying = () => {
      setReady(true);
      setNeedsTap(false);
    };
    v.addEventListener('playing', onPlaying);

    v.srcObject = stream;

    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch((e) => {
        // AbortError bei schnellem Serien-Capture ist harmlos (neuer Load unterbricht play)
        if (e?.name === 'AbortError') return;
        // eslint-disable-next-line no-console
        console.warn('[MobileScannerCamera] play() rejected:', e);
        setNeedsTap(true);
      });
    }

    const t = setTimeout(() => {
      if (v.paused || v.readyState < 2 || v.videoWidth === 0) {
        setNeedsTap(true);
      }
    }, 1800);

    return () => {
      clearTimeout(t);
      v.removeEventListener('playing', onPlaying);
    };
  }, [stream]);

  // Sep 2026 — Volle Kameraauflösung: ImageCapture.takePhoto() (Android/Chrome) liefert ein
  // echtes Foto in Sensor-Auflösung; Fallback: Standbild aus dem (hochauflösenden) Video-Stream.
  const takePhotoFullRes = async () => {
    const track = stream?.getVideoTracks?.()[0];
    if (!track || typeof window.ImageCapture !== 'function') return null;
    try {
      const ic = new window.ImageCapture(track);
      const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error('takePhoto timeout')), 4000));
      const blob = await Promise.race([ic.takePhoto(), timeout]);
      return blob && blob.size > 0 ? blob : null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[MobileScannerCamera] takePhoto failed, using video frame:', e?.message || e);
      return null;
    }
  };

  // Sep 2026 — Upload-Größe begrenzen: lange Seite max. 2500 px (~1 MB statt 4 MB, EXIF-korrekt gedreht).
  const downscaleForUpload = async (blob) => {
    try {
      const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      const s = Math.min(1, MAX_UPLOAD_SIDE / Math.max(bmp.width, bmp.height));
      const w = Math.round(bmp.width * s); const h = Math.round(bmp.height * s);
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(bmp, 0, 0, w, h);
      bmp.close?.();
      const out = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.88));
      return out && out.size > 0 ? out : blob;
    } catch {
      return blob;
    }
  };

  const handleCapture = async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    try {
      let blob = await takePhotoFullRes();
      if (!blob) {
        const v = videoRef.current;
        const off = document.createElement('canvas');
        off.width = v.videoWidth;
        off.height = v.videoHeight;
        off.getContext('2d').drawImage(v, 0, 0);
        blob = await new Promise(res => off.toBlob(res, 'image/jpeg', 0.92));
      }
      blob = await downscaleForUpload(blob);
      setCapturing(false);
      setFlash(true); setTimeout(() => setFlash(false), 180);
      if (navigator.vibrate) { try { navigator.vibrate(30); } catch { /* ignore */ } }
      if (blob && onCapture) onCapture(blob);
    } catch (e) {
      setCapturing(false);
      setError(`Capture fehlgeschlagen: ${e.message}`);
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center p-6 text-white" data-testid="scan-error">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-amber-400" />
          <p className="text-lg font-semibold mb-2">Kamera nicht verfügbar</p>
          <p className="text-sm text-slate-300 mb-6">{error}</p>
          <Button onClick={onCancel} className="bg-white text-black" data-testid="scan-error-close">
            Zurück
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="scan-camera">
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent px-4 py-3 flex items-center justify-between">
        <button onClick={onCancel} className="p-2 -ml-2 text-white" data-testid="scan-cancel-btn">
          <X className="h-6 w-6" />
        </button>
        <div className="text-white text-sm font-medium">
          {quickMode
            ? (quickQueuedCount > 0 ? `Stapel · ${quickQueuedCount} Foto${quickQueuedCount === 1 ? '' : 's'}` : 'Stapel starten')
            : (pageCount > 0 ? `Seite ${pageCount + 1}` : 'Beleg scannen')}
        </div>
        <div className="flex items-center gap-2">
          {quickMode && (
            <button
              onClick={onCancel}
              className="text-xs font-semibold text-emerald-300 bg-emerald-500/15 px-3 py-1.5 rounded-full border border-emerald-500/40"
              data-testid="scan-quick-finish"
            >
              {quickQueuedCount > 0 ? `Fertig (${quickQueuedCount})` : 'Fertig'}
            </button>
          )}
        </div>
      </div>

      <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted autoPlay
        data-testid="scan-video" />
      {flash && <div className="absolute inset-0 bg-white/80 pointer-events-none z-30" data-testid="scan-flash" />}

      {needsTap && (
        <button
          onClick={async () => {
            const v = videoRef.current;
            if (!v) return;
            try {
              v.muted = true;
              await v.play();
              if (v.readyState >= 2 && v.videoWidth > 0) {
                setReady(true);
                setNeedsTap(false);
              }
            } catch (e) {
              setError(`Wiedergabe konnte nicht gestartet werden: ${e.message || e.name}`);
            }
          }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 text-white gap-3"
          data-testid="scan-tap-to-start"
        >
          <CameraIcon className="h-10 w-10 text-emerald-400" />
          <span className="px-5 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-lg">
            Tippen, um Kamera zu starten
          </span>
          <span className="text-xs text-slate-300 max-w-xs text-center">
            iOS Safari benötigt einen Tap, um die Wiedergabe zu starten.
          </span>
        </button>
      )}

      {/* Hilfslinien-Overlay (Belegrahmen-Andeutung) */}
      <div className="absolute inset-x-6 top-20 bottom-32 pointer-events-none border-2 border-dashed border-white/40 rounded-xl" />

      {ready && (
        <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 px-6 text-center pointer-events-none">
          <div className="inline-block bg-black/50 text-white text-xs px-3 py-1.5 rounded-full" data-testid="scan-live-frame-status">
            Beleg ganz ins Bild · gut beleuchten · Zuschnitt folgt nach dem Foto
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-10 pb-8 pt-12 bg-gradient-to-t from-black/80 to-transparent flex flex-col items-center justify-center gap-2">
        {quickMode && (
          <div className="text-[11px] text-white/80 px-3 py-1 rounded-full bg-black/40">
            Auslöser drücken — direkt weiter zum nächsten Beleg. Analyse startet später mit einem Klick.
          </div>
        )}
        <button
          onClick={handleCapture}
          disabled={!ready || capturing}
          className={`relative h-20 w-20 rounded-full border-4 border-white flex items-center justify-center transition-all bg-white/20
            ${capturing ? 'scale-95 opacity-60' : 'active:scale-90'}`}
          data-testid="scan-capture-btn"
          aria-label="Foto aufnehmen"
        >
          {capturing
            ? <Loader2 className="h-8 w-8 text-white animate-spin" />
            : <CameraIcon className="h-8 w-8 text-white" />
          }
        </button>
      </div>
    </div>
  );
}
